import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Search, ChevronRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { departmentsApi } from "@/lib/api";

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await departmentsApi.getAll();
        setDepartments(res.data);
        setFilteredDepartments(res.data);
      } catch (error) {
        console.error("Error loading departments:", error);
      } finally {
        setLoading(false);
      }
    };
    loadDepartments();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = departments.filter(dept =>
        dept.name.toLowerCase().includes(query) ||
        dept.code.includes(query) ||
        dept.region.toLowerCase().includes(query)
      );
      setFilteredDepartments(filtered);
    } else {
      setFilteredDepartments(departments);
    }
  }, [searchQuery, departments]);

  // Group by region
  const departmentsByRegion = filteredDepartments.reduce((acc, dept) => {
    if (!acc[dept.region]) {
      acc[dept.region] = [];
    }
    acc[dept.region].push(dept);
    return acc;
  }, {});

  const regions = Object.keys(departmentsByRegion).sort();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="departments-page">
      <Header />

      {/* Hero */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Tous les <span className="text-[#FFD700]">départements</span>
          </h1>
          <p className="text-slate-400 max-w-2xl">
            Recherchez des professionnels par département ou région en France métropolitaine et DOM-TOM.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-md">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher par nom, code ou région..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                data-testid="department-search-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Departments List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="space-y-8">
            {[...Array(5)].map((_, i) => (
              <div key={i}>
                <div className="skeleton h-6 w-48 mb-4"></div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {[...Array(6)].map((_, j) => (
                    <div key={j} className="skeleton h-12 rounded-lg"></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : filteredDepartments.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Aucun département trouvé
            </h3>
            <p className="text-slate-600">
              Essayez un autre terme de recherche.
            </p>
          </div>
        ) : (
          <div className="space-y-10">
            {regions.map((region) => (
              <div key={region}>
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#FFD700]" />
                  {region}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {departmentsByRegion[region].map((dept) => (
                    <Link
                      key={dept.code}
                      to={`/recherche?department_code=${dept.code}`}
                      className="dept-card bg-white border border-slate-200 rounded-lg p-3 hover:border-[#FFD700] flex items-center justify-between group"
                      data-testid={`department-${dept.code}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono font-bold text-[#2563EB] text-sm">
                          {dept.code}
                        </span>
                        <span className="text-slate-900 text-sm truncate">
                          {dept.name}
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#FFD700] flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
